import type { Configuration, PopupRequest, RedirectRequest } from "@azure/msal-browser"
import { getEnv } from "../utils/getEnv"

/**
 * Azure AD App Registration Configuration
 *
 * Required Environment Variables (see .env.example):
 * - VITE_MSAL_CLIENT_ID: Your Azure AD SPA Application (client) ID
 * - VITE_MSAL_AUTHORITY: Authority URL with your Tenant ID
 * - VITE_MSAL_REDIRECT_URI: Where to redirect after successful authentication
 * - VITE_MSAL_POST_LOGOUT_REDIRECT_URI: Where to redirect after logout
 *
 * To get these values from Azure Portal:
 * 1. Go to Azure Portal > Azure Active Directory > App registrations
 * 2. Select your SPA application
 * 3. Client ID: Found in "Overview" section
 * 4. Tenant ID: Found in "Overview" section (used in Authority URL)
 * 5. Redirect URIs: Configured in "Authentication" section
 */

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 *
 * Note: Uses test-friendly defaults to avoid breaking tests.
 * Validation happens in getMsalInstance() when the app actually runs.
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: getEnv("VITE_MSAL_CLIENT_ID", "test-client-id"),
    authority: getEnv("VITE_MSAL_AUTHORITY", "https://login.microsoftonline.com/test-tenant-id"),
    redirectUri: getEnv("VITE_MSAL_REDIRECT_URI", "http://localhost:3000"),
    postLogoutRedirectUri: getEnv("VITE_MSAL_POST_LOGOUT_REDIRECT_URI", "http://localhost:3000")
  },
  cache: {
    cacheLocation: "localStorage", // Persists across browser sessions for silent token refresh
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    secureCookies: import.meta.env.PROD // Only use secure cookies in production (HTTPS)
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return
        }
        switch (level) {
          case 0: // LogLevel.Error
            // Use console in MSAL config as it's specifically for MSAL logging
            // biome-ignore lint/suspicious/noConsole: MSAL logging requires console
            console.error(`[MSAL] ${message}`)
            return
          case 1: // LogLevel.Warning
            // biome-ignore lint/suspicious/noConsole: MSAL logging requires console
            console.warn(`[MSAL] ${message}`)
            return
          case 2: // LogLevel.Info
            // biome-ignore lint/suspicious/noConsole: MSAL logging requires console
            console.info(`[MSAL] ${message}`)
            return
          case 3: // LogLevel.Verbose
            // biome-ignore lint/suspicious/noConsole: MSAL logging requires console
            console.debug(`[MSAL] ${message}`)
            return
          default:
            return
        }
      }
    }
  }
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 *
 * The API scope is configured via environment variable VITE_MSAL_API_SCOPE.
 * Format: "api://cmt-api-{environment}/access_as_user"
 * - Dev: "api://cmt-api-dev/access_as_user"
 * - Prod: "api://cmt-api-prod/access_as_user"
 */
const apiScope = getEnv("VITE_MSAL_API_SCOPE")
export const loginRequest: RedirectRequest | PopupRequest = {
  scopes: ["openid", "profile", "offline_access", ...(apiScope ? [apiScope] : [])]
}
